import React from 'react';
import { isChunkError } from '../../utils/lazyWithRetry';

/**
 * Top-level Error Boundary. Catches any render-time JS exception that
 * would otherwise blank the entire React tree (white-screen). The most
 * common cause of a white-screen in this app is a ReferenceError /
 * TypeError thrown deep inside a wizard while the user is typing —
 * before this boundary existed, that error would unmount the whole app
 * and leave the user staring at a blank page.
 *
 * This is a defensive structural fix layered on top of the specific
 * bug fix. The specific bug must still be repaired in code; the
 * boundary only ensures the app stays usable instead of going dark.
 */
class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Surface in production logs / Sentry-style hook. Browser console
    // is sufficient for now — gives the user a stack trace to share.
    // eslint-disable-next-line no-console
    console.error('[AppErrorBoundary]', error, info?.componentStack);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleHardReload = () => {
    // Drop the one-reload guard so this click always fetches the new index.html.
    try { window.sessionStorage?.removeItem('aan:chunk-reload-at'); } catch { /* private mode */ }
    window.location.reload();
  };

  handleBack = () => {
    this.setState({ error: null });
    // Best-effort: pop one entry so the user lands on the previous
    // working screen. If they got here by deep-link, reload instead.
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = '/';
    }
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    // A deploy replaces the hashed chunk files under an open tab, so the next navigation asks for
    // a filename that no longer exists. That is not a crash and shouldn't read like one — and the
    // user must not be left on a dead screen that looks like a permissions problem.
    if (isChunkError(error)) {
      return (
        <div
          data-testid="app-error-boundary-chunk"
          className="min-h-screen flex items-center justify-center bg-slate-50 px-4"
        >
          <div className="max-w-lg w-full bg-white rounded-xl shadow-lg border border-slate-200 p-8">
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              A new version was just released
            </h1>
            <p className="text-slate-600 mb-6">
              This tab was still running the previous version. Reloading picks up the new one
              &mdash; you&rsquo;ll stay signed in and nothing was lost.
            </p>
            <button
              type="button"
              onClick={this.handleHardReload}
              data-testid="error-boundary-update-btn"
              className="px-4 py-2 rounded-md bg-[#F97316] text-white font-medium hover:bg-[#ea6a0c]"
            >
              Reload to update
            </button>
          </div>
        </div>
      );
    }

    return (
      <div
        data-testid="app-error-boundary"
        className="min-h-screen flex items-center justify-center bg-slate-50 px-4"
      >
        <div className="max-w-lg w-full bg-white rounded-xl shadow-lg border border-slate-200 p-8">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            Something went wrong on this screen
          </h1>
          <p className="text-slate-600 mb-4">
            We&rsquo;ve been notified and the All American Notice team is
            taking a look. Your data is safe &mdash; nothing was lost.
          </p>
          <details className="mb-6 text-sm text-slate-500 bg-slate-50 rounded p-3 border border-slate-200">
            <summary className="cursor-pointer font-medium">
              Technical details (for support)
            </summary>
            <pre className="mt-2 whitespace-pre-wrap break-words text-xs">
              {String(error?.message || error)}
            </pre>
          </details>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={this.handleBack}
              data-testid="error-boundary-back-btn"
              className="px-4 py-2 rounded-md bg-slate-900 text-white font-medium hover:bg-slate-800"
            >
              Go back
            </button>
            <button
              type="button"
              onClick={this.handleReload}
              data-testid="error-boundary-reload-btn"
              className="px-4 py-2 rounded-md border border-slate-300 text-slate-700 font-medium hover:bg-slate-100"
            >
              Reload page
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default AppErrorBoundary;
